import Link from "next/link";
import { ArrowUpRight } from "lucide-react";

import { cn } from "@/lib/utils";
import { captionStylePath, type CaptionStyle } from "@/lib/caption-styles/content";

import CaptionStyleThumbnail from "./CaptionStyleThumbnail";

const COLUMN_CLASS: Record<number, string> = {
  2: "sm:grid-cols-2",
  3: "grid-cols-2 sm:grid-cols-3",
  4: "sm:grid-cols-2 lg:grid-cols-4",
  6: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6",
};

const columnsForCount = (count: number): number => {
  if (count % 4 === 0) return 4;
  if (count % 3 === 0) return 3;
  return 2;
};

export const CaptionStyleCard = ({
  style,
  compact = false,
}: {
  style: CaptionStyle;
  compact?: boolean;
}) => (
  <Link
    href={captionStylePath(style.slug)}
    prefetch={false}
    className="group flex h-full flex-col overflow-hidden rounded-2xl border border-[#EFF2FF] bg-white shadow-sm transition-colors hover:border-[#335CFF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#335CFF]/40"
  >
    <CaptionStyleThumbnail
      style={style}
      className={cn("border-b border-[#EFF2FF]", compact ? "h-[96px]" : "h-[150px]")}
      imageClassName="w-[82%] transition-transform duration-200 group-hover:scale-[1.04]"
    />
    <div className={cn("flex flex-1 flex-col", compact ? "p-3" : "p-5")}>
      <div className="flex items-start justify-between gap-3">
        <h3
          className={cn(
            "font-semibold leading-[1.25] tracking-tight text-[#101828] transition-colors group-hover:text-[#335CFF]",
            compact ? "text-[14px]" : "text-[17px]",
          )}
        >
          {style.name}
        </h3>
        {!compact && (
          <ArrowUpRight
            className="mt-0.5 size-4 shrink-0 text-[#8B93A7] transition-all duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-[#335CFF]"
            strokeWidth={2}
          />
        )}
      </div>
      {!compact && (
        <p className="mt-2 line-clamp-3 text-[13.5px] leading-[1.6] text-[#525866]">
          {style.descriptor}
        </p>
      )}
      <span
        className={cn(
          "text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[#8B93A7]",
          compact ? "mt-1" : "mt-auto pt-4",
        )}
      >
        {style.grouping === "word" ? "One word" : "Lines"}
      </span>
    </div>
  </Link>
);

const CaptionStyleGrid = ({
  styles,
  columns,
  compact = false,
}: {
  styles: CaptionStyle[];
  columns?: number;
  compact?: boolean;
}) => {
  if (!styles.length) return null;

  const cols = COLUMN_CLASS[columns ?? columnsForCount(styles.length)] ?? COLUMN_CLASS[4];

  return (
    <ul className={cn("grid w-full grid-cols-1 gap-4 sm:gap-5", cols)}>
      {styles.map((style) => (
        <li key={style.slug} className="h-full">
          <CaptionStyleCard style={style} compact={compact} />
        </li>
      ))}
    </ul>
  );
};

export default CaptionStyleGrid;
